import { Directive, forwardRef, input } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';

const START_BEFORE_END = 'startBeforeEnd';

@Directive({
  selector: '[appDateRangeValidator]',
  exportAs: 'appDateRangeValidator',
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DateRangeValidatorDirective),
      multi: true
    }
  ]
})
export class DateRangeValidatorDirective implements Validator {
  /** Key of the form control that holds the start date. For template-driven forms this is the `name` attribute; for reactive forms this is the key in the `FormGroup`. */
  readonly startDateControl = input<string>('');

  /** Key of the form control that holds the end date. For template-driven forms this is the `name` attribute; for reactive forms this is the key in the `FormGroup`. */
  readonly endDateControl = input<string>('');

  // Core validation method called by Angular forms framework.
  // Validates cross-field date range rules and sets errors on individual controls.
  // Returns form-level validation errors if any rules are violated.
  validate(control: AbstractControl): ValidationErrors | null {
    const startControlName = this.startDateControl();
    const endControlName = this.endDateControl();

    // Skip validation if control names haven't been provided yet (during initialization)
    if (!startControlName || !endControlName) {
      return null;
    }

    const startControl = control.get(startControlName);
    const endControl = control.get(endControlName);

    const startDate = this.toDate(startControl?.value);
    const endDate = this.toDate(endControl?.value);

    if (!startDate || !endDate) {
      this.clearValidationErrors(startControl, endControl);
      return null;
    }

    const isStartBeforeEndInvalid = startDate.getTime() >= endDate.getTime();
    this.setControlError(startControl, START_BEFORE_END, isStartBeforeEndInvalid);
    this.setControlError(endControl, START_BEFORE_END, isStartBeforeEndInvalid);

    return isStartBeforeEndInvalid ? { [START_BEFORE_END]: true } : null;
  }

  // Removes all date range validation errors from both start and end controls.
  // Called when validation is disabled or when date values are missing.
  // Ensures a clean slate so previous errors don't persist.
  private clearValidationErrors(startControl: AbstractControl | null, endControl: AbstractControl | null): void {
    this.setControlError(startControl, START_BEFORE_END, false);
    this.setControlError(endControl, START_BEFORE_END, false);
  }

  // Safely converts an unknown value to a Date object.
  // Handles Date instances, ISO strings, and numeric timestamps.
  // Returns null if the value cannot be parsed or is invalid.
  private toDate(value: unknown): Date | null {
    let parsedDate: Date | null = null;

    if (value instanceof Date) {
      parsedDate = Number.isNaN(value.getTime()) ? null : value;
    } else if (typeof value === 'string' || typeof value === 'number') {
      const candidate = new Date(value);
      parsedDate = Number.isNaN(candidate.getTime()) ? null : candidate;
    }

    return parsedDate;
  }

  // Adds or removes a specific validation error from a form control.
  // When isEnabled is true, adds the error key to the control's error map.
  // When isEnabled is false, removes the error key and cleans up if no errors remain.
  private setControlError(control: AbstractControl | null, key: string, isEnabled: boolean): void {
    if (control) {
      const currentErrors: Record<string, unknown> = (control.errors as Record<string, unknown> | null) ?? {};
      const hasKey = key in currentErrors;

      if (isEnabled && !hasKey) {
        control.setErrors({ ...currentErrors, [key]: true });
      }
      else if (!isEnabled && hasKey) {
        const { [key]: removedError, ...remainingErrors } = currentErrors;
        void removedError;
        control.setErrors(Object.keys(remainingErrors).length > 0 ? remainingErrors : null);
      }
    }
  }
}
